/**
 * Apollo Cache Pressure Controls
 *
 * Monitors and manages Apollo InMemoryCache growth to prevent memory leaks.
 * Provides metrics on cache size and entry count, with configurable thresholds
 * to trigger garbage collection.
 *
 * Features:
 * - Estimate cache size in bytes from normalized data
 * - Count cache entries by __typename
 * - Configurable thresholds for size and entry count
 * - Cache metrics for memory telemetry
 *
 * Environment Variables:
 * - CACHE_SIZE_THRESHOLD_MB: Max cache size in MB before gc (default: 50)
 * - CACHE_ENTRY_THRESHOLD: Max cache entries before gc (default: 5000)
 *
 * Usage:
 * - Call checkCacheSize(cache) to get current metrics
 * - Call shouldTriggerCacheGC(cache) to check if gc needed
 * - Use with logCacheStats() from memoryMonitor for telemetry
 */

import type { InMemoryCache } from "@apollo/client"

/**
 * Cache size metrics
 */
export interface CacheMetrics {
  sizeBytes: number
  sizeMB: number
  entryCount: number
  distribution: Record<string, number>
  exceedsThreshold: boolean
  thresholds: {
    sizeMB: number
    entryCount: number
  }
}

/**
 * Default cache thresholds
 */
const DEFAULT_CACHE_SIZE_THRESHOLD_MB = 50
const DEFAULT_CACHE_ENTRY_THRESHOLD = 5000

/**
 * Get cache size threshold from environment
 */
function getCacheSizeThresholdMB(): number {
  const envValue = process.env.CACHE_SIZE_THRESHOLD_MB
  if (!envValue) {
    return DEFAULT_CACHE_SIZE_THRESHOLD_MB
  }

  const parsed = Number(envValue)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_CACHE_SIZE_THRESHOLD_MB
  }

  return parsed
}

/**
 * Get cache entry threshold from environment
 */
function getCacheEntryThreshold(): number {
  const envValue = process.env.CACHE_ENTRY_THRESHOLD
  if (!envValue) {
    return DEFAULT_CACHE_ENTRY_THRESHOLD
  }

  const parsed = Number(envValue)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_CACHE_ENTRY_THRESHOLD
  }

  return parsed
}

/**
 * Estimate cache size in bytes from extracted normalized data
 */
export function getCacheSizeEstimate(cacheData: Record<string, any>): number {
  try {
    const jsonString = JSON.stringify(cacheData)
    return jsonString.length
  } catch (error) {
    console.error("[Cache Pressure] Failed to estimate cache size:", error)
    return 0
  }
}

/**
 * Count cache entries by __typename
 */
export function getTypenameDistribution(
  cacheData: Record<string, any>,
): Record<string, number> {
  const distribution: Record<string, number> = {}

  try {
    for (const key in cacheData) {
      const entry = cacheData[key]
      if (entry && typeof entry === "object" && entry.__typename) {
        const typename = entry.__typename
        distribution[typename] = (distribution[typename] || 0) + 1
      }
    }
  } catch (error) {
    console.error(
      "[Cache Pressure] Failed to get typename distribution:",
      error,
    )
  }

  return distribution
}

/**
 * Check cache size and return metrics
 */
export function checkCacheSize(cache: InMemoryCache): CacheMetrics {
  try {
    const cacheData = cache.extract()
    const sizeBytes = getCacheSizeEstimate(cacheData)
    const sizeMB = sizeBytes / 1024 / 1024
    const distribution = getTypenameDistribution(cacheData)
    const entryCount = Object.keys(cacheData).length

    const thresholds = {
      sizeMB: getCacheSizeThresholdMB(),
      entryCount: getCacheEntryThreshold(),
    }

    const exceedsThreshold =
      sizeMB > thresholds.sizeMB || entryCount > thresholds.entryCount

    return {
      sizeBytes,
      sizeMB,
      entryCount,
      distribution,
      exceedsThreshold,
      thresholds,
    }
  } catch (error) {
    console.error("[Cache Pressure] Failed to check cache size:", error)
    return {
      sizeBytes: 0,
      sizeMB: 0,
      entryCount: 0,
      distribution: {},
      exceedsThreshold: false,
      thresholds: {
        sizeMB: getCacheSizeThresholdMB(),
        entryCount: getCacheEntryThreshold(),
      },
    }
  }
}

/**
 * Check if cache should trigger garbage collection
 */
export function shouldTriggerCacheGC(cache: InMemoryCache): boolean {
  const metrics = checkCacheSize(cache)
  return metrics.exceedsThreshold
}
